import React, { forwardRef } from 'react'
import { motion } from 'framer-motion'
import { BaseCardProps, CardAnimation } from './types'

// Default animations for different card states
const cardAnimations: Record<string, CardAnimation> = {
  default: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    whileHover: { y: -2, scale: 1.02 },
    whileTap: { scale: 0.98 },
    transition: { duration: 0.3, ease: 'easeOut' }
  },
  elevated: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    whileHover: { y: -4, scale: 1.02 },
    whileTap: { scale: 0.98 },
    transition: { duration: 0.3, ease: 'easeOut' }
  },
  outlined: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    whileHover: { scale: 1.02 },
    whileTap: { scale: 0.98 },
    transition: { duration: 0.2, ease: 'easeOut' }
  }
}

// Size variants for consistent spacing
const sizeVariants = {
  sm: 'p-3 rounded-lg',
  md: 'p-4 rounded-xl',
  lg: 'p-6 rounded-2xl'
}

// Visual variants for different card styles
const visualVariants = {
  default: 'bg-white shadow-soft hover:shadow-medium border border-gray-100',
  elevated: 'bg-white shadow-medium hover:shadow-strong border border-gray-100',
  outlined: 'bg-white border-2 border-gray-200 hover:border-primary-300 hover:shadow-soft'
}

interface BaseCardComponent extends BaseCardProps {
  ariaLabel?: string
  ariaDescription?: string
  role?: string
  tabIndex?: number
  onKeyDown?: (event: React.KeyboardEvent) => void
}

const BaseCard = forwardRef<HTMLDivElement, BaseCardComponent>(({
  className = '',
  children,
  onClick,
  disabled = false,
  loading = false,
  variant = 'default',
  size = 'md',
  interactive = true,
  ariaLabel,
  ariaDescription,
  role,
  tabIndex,
  onKeyDown
}, ref) => {
  // Determine if card should be interactive
  const isInteractive = interactive && !!onClick
  
  // Build class names
  const baseClasses = [
    'relative overflow-hidden transition-all duration-300',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
    sizeVariants[size],
    visualVariants[variant],
    isInteractive ? 'cursor-pointer' : '',
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    loading ? 'animate-pulse' : '',
    className
  ].filter(Boolean).join(' ')

  // Animation configuration
  const animation = cardAnimations[variant]
  const motionConfig = {
    initial: animation.initial,
    animate: animation.animate,
    // Disable animations if disabled or loading
    whileHover: disabled || loading ? undefined : animation.whileHover,
    whileTap: disabled || loading ? undefined : animation.whileTap,
    transition: animation.transition
  }

  // Handle click events
  const handleClick = () => {
    if (disabled || loading) return
    onClick?.()
  }

  // Handle keyboard events for accessibility
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || loading) return
    
    // Handle Enter and Space keys for click action
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      event.preventDefault()
      onClick()
    }
    
    onKeyDown?.(event)
  }

  // Accessibility props
  const accessibilityProps = {
    role: role || (isInteractive ? 'button' : undefined),
    tabIndex: disabled ? -1 : (tabIndex ?? (isInteractive ? 0 : undefined)),
    'aria-label': ariaLabel,
    'aria-description': ariaDescription,
    'aria-disabled': disabled || loading
  }

  return (
    <motion.div
      ref={ref}
      className={baseClasses}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...motionConfig}
      {...accessibilityProps}
    >
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        </div>
      )}
      
      {/* Card content */}
      {children}
    </motion.div>
  )
})

BaseCard.displayName = 'BaseCard'

export default BaseCard